import React, {Component} from "react";
import {Button, Card, CardBody, Col, Container, Row,} from "reactstrap";
import http from "../../services/httpService";
import auth from "../../services/authService";
import ReactTable from "react-table";
import {AdminPermission} from "../adminPermission";
import {RefreshStats} from "./refreshStats";
import {
    firstDayOfTheMonthDate,
    formatDate,
    getNewCustomersStat
} from "../../services/dashboardService";
import {DateRangeSearch} from "../common/dateRange";
import {SingleStat} from "./singleStat";

class Dashboard extends Component {
    state = {
        stats: {},
        startDate: firstDayOfTheMonthDate(),
        endDate: formatDate(new Date()),
        newCustomers: {firstTimeCustomers: undefined, sales: undefined},
        isLoading: true,
        year: new Date().getFullYear(),
    }

    async componentDidMount() {
        try {
            if (!auth.isAdmin())
                return;
            const {startDate, endDate} = this.getDateRangeFromUrl();
            const newCustomers = await getNewCustomersStat(startDate, endDate);
            const {data: stats} = await http.get(`dashboard?start=${startDate}&end=${endDate}`);
            this.setState({startDate, newCustomers, endDate, stats, isLoading: false});
        } catch (ex) {
            if (ex.response && ex.response.status === 401) {
                auth.logout();
                window.location.reload(true);
            }
            if (ex.response && ex.response.status === 404) alert("Can't find stats");
        }
    }

    getDateRangeFromUrl = () => {
        //get start and end from querystring
        const params = new URLSearchParams(this.props.location.search);
        const startDate = params.get('start') ? params.get('start') : firstDayOfTheMonthDate();
        const endDate = params.get('end') ? params.get('end') : formatDate(new Date());
        return {startDate, endDate};
    };

    handleRefresh = async () => {
        try {
            await this.handleGetStats();
        } catch (ex) {
            if (ex.response && ex.response.status === 404) alert("Can't find stats");
        }
    };

    handleDateChange = async (e) => {
        this.setState({[e.target.id]: e.target.value});
    };
    handleDatesChange = async (startDate, endDate) => {
        this.setState({isLoading: true});
        const {data: stats} = await http.get(`dashboard?start=${startDate}&end=${endDate}`);
        const newCustomers = await getNewCustomersStat(startDate, endDate);
        this.setState({startDate, endDate, newCustomers, stats, isLoading: false});
    };

    handleGetStats = async () => {
        this.setState({isLoading: true});
        const {startDate, endDate} = this.state;
        const newCustomers = await getNewCustomersStat(startDate, endDate);
        const {data: stats} = await http.get(`dashboard?start=${startDate}&end=${endDate}`);
        this.setState({stats, newCustomers, isLoading: false});
    };

    render() {
        const {isLoading, newCustomers: customersStats, startDate, endDate} = this.state;
        const columns = [
            {id: "cityName", Header: "City", accessor: d => d.cityName},
            {id: "sales", Header: "Sales", accessor: d => d.sales},
            {
                id: "newCustomersAndRenewals",
                Header: "New customers and renewals",
                accessor: d => d.newCustomersAndRenewals
            },
            {
                id: "firstTimeTenantCustomers",
                Header: "First-time customers",
                accessor: d => d.firstTimeTenantCustomers
            },
            {
                id: "searchProfilesCount",
                Header: "Total Search profiles",
                accessor: d => d.searchProfilesCount
            },
            {
                id: "registers",
                Header: "Registers",
                accessor: d => d.registers
            },
            {
                id: "activeCustomers",
                Header: "Total Active customers",
                accessor: d => d.activeCustomers
            },
            {
                id: "parsedHouses",
                Header: "Parsed houses",
                accessor: d => d.parsedHouses
            },
            {
                id: "successRateThisMonth",
                Header: "Success rate",
                accessor: d => d.successRateThisMonth
            }
        ];
        return (
            <Container>
                <AdminPermission user={auth.getCurrentUser()}>
                    <Row className={"mt-2 mb-2"}>
                        <Col>
                            <a href="https://healthchecks.io" target="_blank" rel="noopener noreferrer">
                                <img src="https://healthchecks.io/badge/a095150b-fb81-41af-b0e8-73f277/xD9QDd9x-2.svg" alt="healthchecks.io status" />
                            </a>
                        </Col>
                    </Row>
                    <Row className={"mt-2"}>
                        <Col>
                            <DateRangeSearch startDate={startDate} endDate={endDate}
                                             onDateChange={this.handleDateChange}
                                             onSearch={this.handleGetStats}/>
                        </Col>
                    </Row>
                    <Row className={"my-2"}>
                        <Col>
                            <Button
                                onClick={() =>
                                    this.handleDatesChange(formatDate(new Date()), formatDate(new Date()))
                                }>Today

                            </Button>
                            <Button className={"ml-2"}
                                    onClick={() =>
                                        this.handleDatesChange(firstDayOfTheMonthDate(), formatDate(new Date()))
                                    }>This month

                            </Button>
                            <Button className={"ml-2"}
                                    onClick={() => {
                                        const now = new Date();
                                        const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1, 12);
                                        const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 12);
                                        this.handleDatesChange(formatDate(firstDayLastMonth), formatDate(lastDayLastMonth));
                                    }}>Last month

                            </Button>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="2">
                            <RefreshStats handleRefresh={this.handleRefresh}
                                          isLoading={isLoading}/>
                        </Col>
                    </Row>
                    <Row className={"mt-2"}>
                        <Col>
                            <Card style={{maxWidth: "350px"}}>
                                <CardBody>
                                    <SingleStat className={"m-1"} isLoading={isLoading} name={"First-time customers"} value={customersStats?.firstTimeCustomers} tooltip={"This query identifies first-time customers who:\n - Have only one subscription (indicating they're first-time users)\n - Created their subscription within the specified date range (start and end date included)"}/>
                                    <SingleStat className={"m-1"} isLoading={isLoading} name={"Sales"} 
                                    value={customersStats?.sales} tooltip={"Sum of paid payments from first-time customers (users with only one subscription) within the selected date range."}/>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {!isLoading && (
                        <ReactTable
                            defaultPageSize={30}
                            className="my-3"
                            data={this.state.stats.cityOverviews}
                            loading={isLoading}
                            columns={columns}
                            filterable
                            pageSizeOptions={[30, 50, 100]}
                            minRows={30}
                        />
                    )}
                    {/*<QuarterlyReportForm/>*/}
                </AdminPermission>
            </Container>
        );
    }
}

export default Dashboard;